/**
 * Client-side Plyx branding pipeline.
 *
 * Two public entry points, both backed by the same canvas + MediaRecorder
 * engine so the Plyx wordmark + creator signature + audio "sting" look and
 * sound identical no matter where they're applied:
 *
 *   1. applyPlyxBranding(sourceBlob, opts) → { blob, mime, ext, ok }
 *      Used at UPLOAD time so the master file stored on R2 is already
 *      Plyx-branded — every share, hotlink, or third-party download
 *      carries the watermark + jingle.
 *
 *   2. downloadPlyxVideo({ videoUrl, … }, onProgress)
 *      Used at PLAYBACK time as a creator/viewer "Save" affordance.
 *
 * Why client-side? The host has no ffmpeg binary, and a Python/WASM
 * encoder inside FastAPI is heavy. Modern browsers can do the same job
 * with canvas.captureStream + MediaRecorder + WebAudio — at the cost
 * of ~30s CPU time per upload, which we surface as a progress UI.
 *
 * Graceful degradation: every pipeline catches its own failure and
 * returns ok:false so callers can decide to upload the original bytes
 * untouched rather than block the user.
 */

const PLYX_PINK = "#FF2D7A";
const PLYX_AMBER = "#FFB42D";
const PLYX_TEAL = "#00E0C7";

function pickRecorderMime() {
  // iter-119mm: PRIORITIZE MP4 over WebM. WhatsApp Status, Samsung
  // Gallery, and iOS share sheet all accept MP4 universally but reject
  // WebM on many devices ("Info Code 4106" on Samsung). Modern Chrome
  // Android v121+ and iOS Safari 14.1+ both support native MP4
  // recording via MediaRecorder — this ordering makes those users get
  // the guaranteed-compatible file. WebM is kept as a fallback ONLY
  // for older browsers (Firefox, Chrome < v121) where it's the only
  // option — better a WebM watermark than no watermark at all.
  const candidates = [
    "video/mp4;codecs=avc1.42E01E,mp4a.40.2",  // H.264 Baseline + AAC LC
    "video/mp4;codecs=avc1.42E01E",             // video-only H.264 Baseline
    "video/mp4",                                // let browser pick codec
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  if (typeof MediaRecorder === "undefined") return null;
  for (const m of candidates) {
    try {
      if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) return m;
    } catch { /* ignore */ }
  }
  return null;
}

/** Draw the Plyx wordmark + creator signature pill into the given 2d ctx.
 *  iter-119d: per product spec, the main visible badge is `plyx @handle`
 *  whenever a handle is available. The small subtitle keeps the URL hint.
 *  Matches the backend ffmpeg watermark text-for-text so a server-built
 *  AND a client-built download carry the same identifying badge. */
function drawPlyxWatermark(ctx, { w, h, handle }) {
  const pad = Math.max(12, Math.round(w * 0.018));
  const fontSize = Math.max(14, Math.round(w * 0.028));
  const sigSize = Math.max(10, Math.round(w * 0.018));
  const cleanHandle = (handle || "").toString().trim().replace(/^@+/, "").slice(0, 32);
  const text = cleanHandle ? `plyx @${cleanHandle}` : "Plyx";
  const sig = cleanHandle ? "plyx.app" : "plyx.app";

  ctx.save();
  ctx.font = `900 ${fontSize}px -apple-system, "Segoe UI", Roboto, sans-serif`;
  const textW = ctx.measureText(text).width;
  ctx.font = `600 ${sigSize}px -apple-system, "Segoe UI", Roboto, sans-serif`;
  const sigW = ctx.measureText(sig).width;
  const boxW = Math.max(textW, sigW) + pad * 2;
  const boxH = fontSize + sigSize + pad * 1.6;
  // Bottom-LEFT corner (user spec). Was bottom-right previously — TikTok
  // and IG default to right, but Plyx is its own brand and bottom-left
  // is what the founder asked for. The pad gives breathing room from
  // the screen edge regardless of safe-area on mobile.
  const x = pad;
  const y = h - boxH - pad;

  ctx.globalAlpha = 0.62;
  ctx.fillStyle = "#000";
  const r = boxH / 2;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + boxW - r, y);
  ctx.quadraticCurveTo(x + boxW, y, x + boxW, y + r);
  ctx.lineTo(x + boxW, y + boxH - r);
  ctx.quadraticCurveTo(x + boxW, y + boxH, x + boxW - r, y + boxH);
  ctx.lineTo(x + r, y + boxH);
  ctx.quadraticCurveTo(x, y + boxH, x, y + boxH - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
  ctx.globalAlpha = 1;

  const grd = ctx.createLinearGradient(x, y, x + boxW, y + boxH);
  grd.addColorStop(0, PLYX_PINK);
  grd.addColorStop(0.55, PLYX_AMBER);
  grd.addColorStop(1, PLYX_TEAL);
  ctx.fillStyle = grd;
  ctx.font = `900 ${fontSize}px -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.textBaseline = "top";
  ctx.fillText(text, x + pad, y + pad * 0.4);

  ctx.fillStyle = "#FFFFFF";
  ctx.globalAlpha = 0.92;
  ctx.font = `600 ${sigSize}px -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.fillText(sig, x + pad, y + pad * 0.4 + fontSize + 2);
  ctx.restore();
}

/** Synthesized 3-note Plyx jingle — A5 → C#6 → E6 arpeggio + envelope. */
function playPlyxJingle(audioCtx, dest, startTime) {
  const notes = [
    { freq: 880.0,  t: 0.0,  dur: 0.18 },
    { freq: 1108.7, t: 0.18, dur: 0.18 },
    { freq: 1318.5, t: 0.36, dur: 0.42 },
  ];
  const gainMaster = audioCtx.createGain();
  gainMaster.gain.value = 0.0;
  gainMaster.connect(dest);
  gainMaster.gain.setValueAtTime(0.0, startTime);
  gainMaster.gain.linearRampToValueAtTime(0.4, startTime + 0.03);
  gainMaster.gain.setValueAtTime(0.4, startTime + 0.6);
  gainMaster.gain.linearRampToValueAtTime(0.0, startTime + 0.85);

  notes.forEach((n) => {
    const osc1 = audioCtx.createOscillator();
    osc1.type = "triangle";
    osc1.frequency.value = n.freq;
    const osc2 = audioCtx.createOscillator();
    osc2.type = "sine";
    osc2.frequency.value = n.freq * 2;
    const g = audioCtx.createGain();
    g.gain.value = 0.0;
    osc1.connect(g);
    osc2.connect(g);
    g.connect(gainMaster);
    const t0 = startTime + n.t;
    g.gain.setValueAtTime(0.0, t0);
    g.gain.linearRampToValueAtTime(0.6, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + n.dur);
    osc1.start(t0);
    osc2.start(t0);
    osc1.stop(t0 + n.dur + 0.01);
    osc2.stop(t0 + n.dur + 0.01);
  });
}

function triggerDownload(href, filename) {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => a.remove(), 0);
}

async function fallbackDirectDownload(url, filename) {
  try {
    const r = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const b = await r.blob();
    const obj = URL.createObjectURL(b);
    triggerDownload(obj, filename);
    setTimeout(() => URL.revokeObjectURL(obj), 60_000);
  } catch {
    window.open(url, "_blank", "noopener");
  }
}

/** Composite the Plyx watermark on a single still image → JPEG Blob. */
async function brandImageBlob(sourceBlob, { handle, onProgress }) {
  onProgress?.({ phase: "loading", progress: 0.1 });
  const objUrl = URL.createObjectURL(sourceBlob);
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((res, rej) => {
      img.onload = res;
      img.onerror = rej;
      img.src = objUrl;
    });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || 1080;
    canvas.height = img.naturalHeight || 1920;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    drawPlyxWatermark(ctx, { w: canvas.width, h: canvas.height, handle });
    onProgress?.({ phase: "encoding", progress: 0.7 });
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
    if (!blob) throw new Error("encode-failed");
    onProgress?.({ phase: "done", progress: 1 });
    return { blob, mime: "image/jpeg", ext: "jpg", ok: true };
  } finally {
    URL.revokeObjectURL(objUrl);
  }
}

/** Draw the closing Plyx outro card — gradient wordmark + signature
 * tag-line. Same gradient/typography as the intro bumper so the brand
 * bookends feel intentional. ``tNorm`` is the eased 0→1 progress into
 * the outro (used for a subtle scale-in). */
function drawPlyxOutro(ctx, { w, h, handle, tNorm }) {
  ctx.fillStyle = "#0A0A0A";
  ctx.fillRect(0, 0, w, h);
  const s = 0.6 + Math.min(1, tNorm * 2) * 0.4;
  ctx.save();
  ctx.translate(w / 2, h / 2);
  ctx.scale(s, s);
  const grd = ctx.createLinearGradient(-w / 2, -h / 2, w / 2, h / 2);
  grd.addColorStop(0, PLYX_PINK);
  grd.addColorStop(0.5, PLYX_AMBER);
  grd.addColorStop(1, PLYX_TEAL);
  ctx.fillStyle = grd;
  ctx.font = `900 ${Math.round(w * 0.18)}px -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.globalAlpha = Math.min(1, tNorm * 2);
  ctx.fillText("Plyx", 0, 0);
  ctx.restore();
  ctx.globalAlpha = 0.95;
  ctx.fillStyle = "#FFFFFF";
  ctx.font = `600 ${Math.round(w * 0.034)}px -apple-system, sans-serif`;
  ctx.textAlign = "center";
  ctx.fillText(handle ? `@${handle} on plyx.app` : "plyx.app", w / 2, h / 2 + h * 0.13);
  ctx.globalAlpha = 0.75;
  ctx.font = `500 ${Math.round(w * 0.026)}px -apple-system, sans-serif`;
  ctx.fillText("where moments become movements", w / 2, h / 2 + h * 0.185);
  ctx.globalAlpha = 1;
}

/** Composite Plyx watermark + jingle bumper into a video → webm/mp4 Blob.
 *
 * `prerollSec` controls how long the gradient "Plyx" title sits before
 * the original video starts. Shorter (~0.6s) is friendlier at upload
 * time so creators don't feel their clip is delayed; longer (~1.0s)
 * is fine at download time as a sting.
 *
 * `outroSec` controls the closing Plyx signature card that's appended
 * AFTER the source video ends. Set to 0 to skip (used at upload time
 * to keep the master file tight; download time defaults to 1.4s so the
 * downloaded clip always carries the Plyx outro signature).
 */
async function brandVideoBlob(sourceBlob, { handle, prerollSec = 1.0, outroSec = 1.4, maxRecordSec = 90, onProgress }) {
  const mime = pickRecorderMime();
  if (!mime || typeof HTMLCanvasElement.prototype.captureStream !== "function") {
    throw new Error("recorder-unsupported");
  }
  const sourceObjUrl = URL.createObjectURL(sourceBlob);
  const video = document.createElement("video");
  video.crossOrigin = "anonymous";
  video.muted = false;
  video.playsInline = true;
  video.src = sourceObjUrl;
  await new Promise((res, rej) => {
    video.onloadedmetadata = res;
    video.onerror = () => rej(new Error("video-load-failed"));
  });

  // Hard cap output dimensions to 1080×1920 so the recorder doesn't
  // chew on huge source files. Aspect ratio is preserved.
  const maxW = 1080, maxH = 1920;
  const scale = Math.min(1, maxW / (video.videoWidth || 720), maxH / (video.videoHeight || 1280));
  const dw = Math.max(2, Math.round((video.videoWidth || 720) * scale));
  const dh = Math.max(2, Math.round((video.videoHeight || 1280) * scale));

  const canvas = document.createElement("canvas");
  canvas.width = dw;
  canvas.height = dh;
  const ctx = canvas.getContext("2d");
  const videoStream = canvas.captureStream(30);

  const AC = window.AudioContext || window.webkitAudioContext;
  const audioCtx = new AC();
  const dest = audioCtx.createMediaStreamDestination();
  try {
    const src = audioCtx.createMediaElementSource(video);
    src.connect(dest);
    src.connect(audioCtx.destination); // monitor
  } catch {
    // No audio capture available — record silent video.
  }

  const combined = new MediaStream();
  videoStream.getVideoTracks().forEach((t) => combined.addTrack(t));
  dest.stream.getAudioTracks().forEach((t) => combined.addTrack(t));

  const recorder = new MediaRecorder(combined, { mimeType: mime, videoBitsPerSecond: 4_500_000 });
  const chunks = [];
  recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };

  const drawBumper = (elapsed) => {
    ctx.fillStyle = "#0A0A0A";
    ctx.fillRect(0, 0, dw, dh);
    const tNorm = Math.min(1, elapsed / prerollSec);
    const s = 0.5 + tNorm * 0.5;
    ctx.save();
    ctx.translate(dw / 2, dh / 2);
    ctx.scale(s, s);
    const grd = ctx.createLinearGradient(-dw / 2, -dh / 2, dw / 2, dh / 2);
    grd.addColorStop(0, PLYX_PINK);
    grd.addColorStop(0.5, PLYX_AMBER);
    grd.addColorStop(1, PLYX_TEAL);
    ctx.fillStyle = grd;
    ctx.font = `900 ${Math.round(dw * 0.16)}px -apple-system, "Segoe UI", Roboto, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.globalAlpha = tNorm;
    ctx.fillText("Plyx", 0, 0);
    ctx.restore();
    ctx.globalAlpha = 0.7;
    ctx.fillStyle = "#FFFFFF";
    ctx.font = `500 ${Math.round(dw * 0.028)}px -apple-system, sans-serif`;
    ctx.textAlign = "center";
    ctx.fillText(handle ? `featuring @${handle}` : "plyx.app", dw / 2, dh / 2 + dh * 0.12);
    ctx.globalAlpha = 1;
  };

  let stopped = false;
  let inOutro = false;
  let prerollStart = 0;
  let outroStart = 0;
  const renderFrame = () => {
    if (stopped) return;
    const now = performance.now() / 1000;
    if (now - prerollStart < prerollSec) {
      drawBumper(now - prerollStart);
    } else if (inOutro) {
      const elapsed = now - outroStart;
      const tNorm = outroSec > 0 ? Math.min(1, elapsed / outroSec) : 1;
      drawPlyxOutro(ctx, { w: dw, h: dh, handle, tNorm });
    } else {
      ctx.drawImage(video, 0, 0, dw, dh);
      drawPlyxWatermark(ctx, { w: dw, h: dh, handle });
    }
    requestAnimationFrame(renderFrame);
  };

  recorder.start(250);
  prerollStart = performance.now() / 1000;
  renderFrame();
  try {
    await audioCtx.resume();
    playPlyxJingle(audioCtx, dest, audioCtx.currentTime + 0.02);
  } catch { /* jingle is best-effort */ }

  setTimeout(() => {
    video.currentTime = 0;
    video.play().catch(() => {});
  }, prerollSec * 1000);

  await new Promise((resolve) => {
    const finish = () => {
      if (stopped) return;
      stopped = true;
      try { recorder.requestData(); } catch { /* ignore */ }
      try { recorder.stop(); } catch { /* ignore */ }
      try { video.pause(); } catch { /* ignore */ }
      try { audioCtx.close(); } catch { /* ignore */ }
      resolve();
    };
    // When the source video ends (or we hit the recording cap), enter
    // the OUTRO phase: keep the recorder running for `outroSec` more
    // seconds while the canvas paints the closing Plyx signature card
    // and the brand jingle plays. THEN stop. This guarantees every
    // downloaded clip ends on a Plyx mark — the bug previously was
    // that ``ended`` triggered an immediate ``recorder.stop`` so the
    // outro never made it into the file.
    const beginOutro = () => {
      if (stopped || inOutro) return;
      if (outroSec <= 0) { finish(); return; }
      inOutro = true;
      outroStart = performance.now() / 1000;
      try { video.pause(); } catch { /* ignore */ }
      try { playPlyxJingle(audioCtx, dest, audioCtx.currentTime + 0.05); }
      catch { /* jingle is best-effort */ }
      setTimeout(finish, outroSec * 1000 + 100);
    };
    video.addEventListener("ended", beginOutro, { once: true });
    const cap = setTimeout(beginOutro, maxRecordSec * 1000);
    video.addEventListener("ended", () => clearTimeout(cap), { once: true });
    if (onProgress) {
      const dur = video.duration || 0;
      const tick = setInterval(() => {
        if (stopped) { clearInterval(tick); return; }
        const total = (dur > 0 ? dur : 0) + prerollSec + outroSec;
        const cur = inOutro
          ? (dur + prerollSec + (performance.now() / 1000 - outroStart))
          : (video.currentTime + prerollSec);
        const p = total > 0 ? Math.min(0.95, cur / total) : 0.5;
        onProgress({ phase: inOutro ? "outro" : "recording", progress: 0.1 + p * 0.85 });
      }, 250);
    }
  });

  await new Promise((res) => {
    if (recorder.state === "inactive") return res();
    recorder.onstop = res;
  });

  URL.revokeObjectURL(sourceObjUrl);
  if (!chunks.length) throw new Error("no-chunks");
  const baseMime = mime.split(";")[0];
  const ext = baseMime.includes("mp4") ? "mp4" : "webm";
  return {
    blob: new Blob(chunks, { type: baseMime }),
    mime: baseMime,
    ext,
    ok: true,
  };
}

/** Public — apply Plyx branding to an arbitrary File/Blob and return the
 * branded Blob. Falls back to ok:false so callers can keep the original. */
export async function applyPlyxBranding(sourceBlob, opts = {}) {
  const { mediaType = "video", handle, onProgress, prerollSec, outroSec, maxRecordSec } = opts;
  if (!sourceBlob) return { blob: null, mime: null, ext: null, ok: false, error: "no-source" };
  try {
    if (mediaType === "photo") {
      return await brandImageBlob(sourceBlob, { handle, onProgress });
    }
    return await brandVideoBlob(sourceBlob, {
      handle,
      onProgress,
      prerollSec: typeof prerollSec === "number" ? prerollSec : 0.8,
      // Upload-time default: no outro so the stored master stays tight.
      // The server-side branding pipeline appends the proper outro when
      // a viewer hits Share/Download. Callers at download time pass an
      // explicit ``outroSec`` to opt in.
      outroSec: typeof outroSec === "number" ? outroSec : 0,
      maxRecordSec: typeof maxRecordSec === "number" ? maxRecordSec : 90,
    });
  } catch (e) {
    console.warn("Plyx branding failed:", e);
    return { blob: null, mime: null, ext: null, ok: false, error: String(e) };
  }
}

/** Public — download a remote video/photo URL with the Plyx watermark.
 *
 * Options:
 *   - whatsappStatus (bool): cap output at ~29s and drop the intro so
 *     the file fits WhatsApp Status' 30-second limit AND still ends on
 *     the Plyx outro card. Without this, WhatsApp Status auto-trims
 *     longer clips mid-scene, cutting off the interesting part.
 */
export async function downloadPlyxVideo({ videoUrl, handle, videoId, mediaType, whatsappStatus = false }, onProgress) {
  if (!videoUrl) throw new Error("no-url");
  // Fetch first so the watermarker doesn't need to fight CORS itself.
  let sourceBlob = null;
  try {
    const r = await fetch(videoUrl, { mode: "cors", credentials: "omit" });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    sourceBlob = await r.blob();
  } catch (e) {
    console.warn("Plyx download fetch failed, falling back direct:", e);
    const isPhoto = mediaType === "photo";
    await fallbackDirectDownload(videoUrl, `plyx_${handle || "creator"}_${videoId}.${isPhoto ? "jpg" : "mp4"}`);
    return { watermarked: false };
  }
  // iter-119nn: WhatsApp Status caps videos at 30 seconds. If the
  // source is longer, WhatsApp truncates it mid-scene. Trimming to
  // ~28s of video + 1.2s outro = 29.2s total leaves headroom and
  // guarantees the outro is what viewers see last on Status.
  const brandingOpts = whatsappStatus
    ? {
        mediaType,
        handle,
        onProgress,
        prerollSec: 0,        // skip intro — every second matters at 30s cap
        outroSec: 1.2,        // short brand outro
        maxRecordSec: 28,     // 28s source + 1.2s outro = 29.2s total
      }
    : {
        mediaType,
        handle,
        onProgress,
        prerollSec: 1.0,
        // Download path always gets the closing Plyx signature card so the
        // user-saved file ends on the brand — matches what the server-side
        // ffmpeg pipeline bakes in for the cached branded MP4.
        outroSec: 1.4,
        maxRecordSec: 90,
      };
  const result = await applyPlyxBranding(sourceBlob, brandingOpts);
  if (!result.ok || !result.blob) {
    const isPhoto = mediaType === "photo";
    await fallbackDirectDownload(videoUrl, `plyx_${handle || "creator"}_${videoId}.${isPhoto ? "jpg" : "mp4"}`);
    return { watermarked: false };
  }
  const suffix = whatsappStatus ? "-status" : "";
  const objUrl = URL.createObjectURL(result.blob);
  triggerDownload(objUrl, `plyx_${handle || "creator"}_${videoId}${suffix}.${result.ext}`);
  setTimeout(() => URL.revokeObjectURL(objUrl), 120_000);
  return { watermarked: true };
}
